import React, { ReactNode } from 'react';
import { Box } from '@mui/material';
import { cardArtFallback } from '../../NFTCollection/components/CardInspectModal';

// Shared arena styling for the marketplace cards (browse / grouped / my
// listings) so they read as one system.
export const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

// In-game rarity accents (matches getRarityColor in Marketplace.tsx).
export const rarityHex = (r?: string | null): string =>
  (
    ({
      common: '#6b7280',
      rare: '#d946ef',
      epic: '#22c55e',
      legendary: '#ef4444',
      relic: '#eab308'
    } as Record<string, string>)[String(r || '').toLowerCase()] || '#6b7280'
  );

export const cardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid rgba(56, 232, 255, 0.16)',
  background: 'linear-gradient(180deg, rgba(11, 17, 25, 0.85), rgba(7, 11, 17, 0.9))',
  boxShadow: '0 18px 40px -30px rgba(0, 0, 0, 0.9)',
  transition:
    'transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: 'rgba(56, 232, 255, 0.45)',
    boxShadow: '0 22px 50px -28px rgba(56, 232, 255, 0.35)'
  }
} as const;

export const nameSx = {
  fontWeight: 800,
  fontSize: 16,
  lineHeight: 1.2,
  color: '#e8f4fb'
} as const;

export const kickerSx = {
  mt: 0.5,
  font: `700 10px ${MONO}`,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: '#8ea3b3'
} as const;

export const subSx = {
  font: `600 11px ${MONO}`,
  color: '#5c6b78'
} as const;

export const cyanBtnSx = {
  fontWeight: 800,
  color: '#04222a',
  backgroundColor: '#38e8ff',
  borderRadius: '9px',
  py: 1,
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#5fefff', boxShadow: 'none' }
} as const;

// A big gold price with an INC suffix.
export const priceValueSx = {
  font: `900 22px ${MONO}`,
  color: '#ffcf40',
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1
} as const;

export const StatPill = ({
  label,
  value
}: {
  label: string;
  value: number | string;
}) => (
  <Box
    sx={{
      px: 1,
      py: 0.4,
      borderRadius: '8px',
      background: 'rgba(56, 232, 255, 0.07)',
      border: '1px solid rgba(56, 232, 255, 0.18)',
      font: `700 11px ${MONO}`,
      color: '#c9dbe6'
    }}
  >
    <Box component="span" sx={{ color: '#7f8ea0' }}>
      {label}{' '}
    </Box>
    {value}
  </Box>
);

export const Pill = ({
  color,
  children,
  solid = false
}: {
  color: string;
  children: ReactNode;
  solid?: boolean;
}) => (
  <Box
    sx={{
      px: 1,
      py: 0.4,
      borderRadius: '999px',
      font: `800 9px ${MONO}`,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      color: solid ? '#04222a' : color,
      background: solid ? color : `${color}22`,
      border: solid ? 'none' : `1px solid ${color}66`
    }}
  >
    {children}
  </Box>
);

// Art tile with a rarity-tinted fallback orb (shown if the image 404s), a bottom
// scrim, and optional corner badges.
export const NftArt = ({
  imgSrc,
  alt,
  rarityColor,
  topLeft,
  topRight
}: {
  imgSrc?: string;
  alt: string;
  rarityColor: string;
  topLeft?: ReactNode;
  topRight?: ReactNode;
}) => (
  <Box
    sx={{
      position: 'relative',
      aspectRatio: '4 / 3.05',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'radial-gradient(120% 90% at 50% 28%, #1b3350, #0a0f16 72%)'
    }}
  >
    <Box
      sx={{
        width: 66,
        height: 66,
        borderRadius: '50%',
        background: `radial-gradient(circle at 42% 38%, ${rarityColor}, #0b2740 72%)`,
        boxShadow: `0 0 30px ${rarityColor}55`
      }}
    />
    {imgSrc ? (
      <Box
        component="img"
        src={imgSrc}
        alt={alt}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }}
        onError={(e) => {
          // the CDN failed: the bucket copy once (cardArtFallback), then nothing
          if (!cardArtFallback(e.currentTarget as HTMLImageElement))
            (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    ) : null}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, transparent 50%, rgba(5, 8, 12, 0.82))'
      }}
    />
    {topLeft && <Box sx={{ position: 'absolute', top: 10, left: 10 }}>{topLeft}</Box>}
    {topRight && (
      <Box sx={{ position: 'absolute', top: 10, right: 10 }}>{topRight}</Box>
    )}
  </Box>
);
